import { HStack, Text } from "@chakra-ui/react";
import type { MFCampaign } from "state/members/accessReviews";
import {
  daysUntil,
  dueDayLabel,
  startedAgoLabel,
  type StatusPillKind,
} from "../reviewLogic";
import ReviewStatusPill from "./ReviewStatusPill";
import RevisionChip from "./RevisionChip";
import OverdueBlip from "./OverdueBlip";

// Re-exported so existing consumers (e.g. the Home AccessReviewsCard) keep
// importing the blip from here.
export { OverdueBlip };

/**
 * CampaignMeta — the one meta line shared by the campaign card and the drawer
 * header (PLO-4803): status pill, optional revision chip, then a thin tertiary
 * "· Started Nd ago ·" and the due state. Overdue is derived (open + past due)
 * and rendered as a red pulsing blip + "Overdue by Nd" — never a pill. Due
 * within 3 days emphasises amber. Ports design.tsx `CampaignMeta`
 * (~lines 1701–1727).
 */
export interface CampaignMetaProps {
  campaign: Pick<MFCampaign, "insertedAt" | "dueDate" | "status">;
  /** the status-pill kind, derived by the caller (which knows review rollups) */
  statusKind: StatusPillKind;
  /** how many resource reviews need revision (omit/0 → no revision chip) */
  revisionCount?: number;
}

const CampaignMeta = ({
  campaign,
  statusKind,
  revisionCount = 0,
}: CampaignMetaProps) => {
  const days = daysUntil(campaign.dueDate);
  const notStarted = campaign.status === "draft";
  const overdue = campaign.status !== "completed" && days < 0;

  return (
    <HStack spacing="xs" align="center" flexWrap="wrap">
      <ReviewStatusPill kind={statusKind} />
      {revisionCount > 0 && <RevisionChip count={revisionCount} />}

      {!notStarted && (
        <Text fontSize="sm" color="text.body.tertiary">
          · Started {startedAgoLabel(campaign.insertedAt)} ·
        </Text>
      )}

      {/* Completed cycles carry no reliable completion date from the API, so we
          omit the due/completed text and let the green "Complete" pill stand. */}
      {campaign.status === "completed" ? null : notStarted ? (
        <Text fontSize="sm" color="text.body.tertiary">
          · {dueDayLabel(days)}
        </Text>
      ) : overdue ? (
        <HStack spacing="sm">
          <OverdueBlip />
          <Text fontSize="sm" fontWeight={500} color="text.body.error">
            {dueDayLabel(days)}
          </Text>
        </HStack>
      ) : (
        <Text
          fontSize="sm"
          fontWeight={days <= 3 ? 500 : 400}
          color={days <= 3 ? "text.body.warning" : "text.body.tertiary"}
        >
          {dueDayLabel(days)}
        </Text>
      )}
    </HStack>
  );
};

export default CampaignMeta;
